import axios from 'axios';
import { Toast } from 'vant';

const http = axios.create({ baseURL: '/api', timeout: 30000 });

http.interceptors.request.use(config => {
  const auth = getAuth();
  if (auth && auth.token) config.headers.Authorization = 'Bearer ' + auth.token;
  return config;
});

/**
 * 后端 BizError code → 前端 Toast 提示映射
 *
 * 分类：
 *   warn  (黄色) - 需要用户修正但不是错：发票号重复、参数缺失、流程类
 *   error (红色) - 阻断：额度/状态/系统错误
 *   info  (蓝色) - 提示：状态类、未找到
 *
 * 每条可包含 prefix（附加在 message 前的简短标签，提升可读性）
 */
const BIZ_CODE_TOAST = {
  // ---- 额度/状态：阻断 ----
  OVER_AVAILABLE:     { type: 'error', prefix: '额度超限' },
  NO_AVAILABLE:       { type: 'error', prefix: '无可开票额度' },
  INSUFFICIENT:       { type: 'error', prefix: '余额不足' },
  NO_PENDING_EXPENSE: { type: 'error', prefix: '无可开记录' },
  STATE_FORBIDDEN:    { type: 'error', prefix: '状态不允许' },
  STATE_INVALID:      { type: 'error', prefix: '状态非法' },
  STATE_UNKNOWN:      { type: 'error', prefix: '状态异常' },
  LEDGER_TYPE_INVALID:{ type: 'error', prefix: '流水异常' },

  // ---- 发票号相关：需要用户修正 ----
  DUP_INVOICE_NO:     { type: 'warn',  prefix: '发票号重复' },
  BAD_DETAIL:         { type: 'warn',  prefix: '发票明细' },
  BAD_AMOUNT:         { type: 'warn',  prefix: '金额错误' },

  // ---- 表单参数：用户修正 ----
  BAD_INPUT:          { type: 'warn',  prefix: '请检查输入' },
  BAD_CATEGORY:       { type: 'warn',  prefix: '品类错误' },
  BAD_LINK:           { type: 'warn',  prefix: '链接错误' },
  BAD_ACCOUNT:        { type: 'warn',  prefix: '账号错误' },
  BAD_COMPANY:        { type: 'warn',  prefix: '公司名错误' },
  BAD_USERNAME:       { type: 'warn',  prefix: '账号格式错误' },
  NO_ACCOUNT:         { type: 'warn',  prefix: '请选择入账账号' },
  NO_FILES:           { type: 'warn',  prefix: '请上传附件' },
  TOO_MANY_FILES:     { type: 'warn',  prefix: '附件过多' },
  DUPLICATE:          { type: 'warn',  prefix: '重复提交' },
  FIELD_MISSING:      { type: 'warn',  prefix: '缺少字段' },
  FIELD_TYPE:         { type: 'warn',  prefix: '字段类型错误' },
  FIELD_RANGE:        { type: 'warn',  prefix: '字段取值错误' },

  // ---- 资源/查找 ----
  NOT_FOUND:          { type: 'info',  prefix: '记录不存在' },
  FORBIDDEN:          { type: 'error', prefix: '禁止操作' },

  // ---- 系统错误 ----
  INTERNAL:           { type: 'error', prefix: '系统错误' },
  DB_ERROR:           { type: 'error', prefix: '数据库错误' }
};

/**
 * 弹业务错误 toast
 *   - warn  → Toast.warning（黄色图标，柔和提示用户修正）
 *   - error → Toast.fail（红色，阻断）
 *   - info  → Toast（中性，提示类）
 *   - 未知 code → 兜底 Toast.fail（确保不静默）
 */
function showBizToast(code, message) {
  const cfg = BIZ_CODE_TOAST[code] || { type: 'error', prefix: '' };
  const text = cfg.prefix ? `${cfg.prefix}：${message}` : message;
  const dur = 4000;
  try {
    if (cfg.type === 'warn') {
      // vant 2 Toast 支持 type='warning'，会自动显示 ⚠ 图标 + 蓝色背景
      Toast({ type: 'warning', message: text, duration: dur });
    } else if (cfg.type === 'info') {
      Toast({ message: text, duration: 3000 });
    } else {
      Toast.fail({ message: text, duration: dur });
    }
  } catch (_) {
    // 兜底：vant 异常时退化为纯文本
    Toast(text);
  }
}

http.interceptors.response.use(
  res => res.data,
  err => {
    const status = err.response && err.response.status;
    const data = err.response && err.response.data;
    const msg = (data && data.message) || '网络错误，请稍后重试';
    const code = data && data.code;

    // 401 强制重新登录
    if (status === 401) {
      clearAuth();
      const isAdmin = location.hash.startsWith('#/admin');
      Toast.fail('登录已过期，请重新登录');
      setTimeout(() => { location.hash = isAdmin ? '#/admin/login' : '#/login'; }, 800);
      return Promise.reject(err);
    }

    // 业务错误：弹 toast（按 code 分类显示），但仍向上 reject 让业务方必要时附加 UI 处理
    if (code) {
      showBizToast(code, msg);
      // 标记业务方可用字段，便于后续扩展（不改 catch 行为）
      err.bizCode = code;
      err.bizMessage = msg;
      return Promise.reject(err);
    }

    // 网络/未知错误：弹 toast
    if (status >= 500) {
      Toast.fail({ message: '服务器开小差了，请稍后重试', duration: 3000 });
    } else if (status === 404) {
      Toast.fail('请求的资源不存在');
    } else if (status === 403) {
      Toast.fail('没有权限执行该操作');
    } else if (!err.response) {
      Toast.fail('网络连接失败，请检查网络');
    } else {
      Toast.fail(msg);
    }
    return Promise.reject(err);
  }
);

const AUTH_KEY = 'st_energy_auth';

export function saveAuth(data) {
  localStorage.setItem(AUTH_KEY, JSON.stringify(data));
}
export function getAuth() {
  try {
    const a = JSON.parse(localStorage.getItem(AUTH_KEY));
    if (a) return a;
  } catch (e) {}
  // 兼容旧 key 'token' / 'user'
  const legacy = localStorage.getItem('token');
  if (legacy) {
    try {
      const obj = JSON.parse(legacy);
      if (obj && obj.token) {
        // 自动迁移到新 key
        localStorage.setItem(AUTH_KEY, JSON.stringify(obj));
        return obj;
      }
    } catch (e) {}
  }
  try {
    const u = localStorage.getItem('user');
    if (u) {
      const obj = JSON.parse(u);
      if (obj && obj.token) {
        localStorage.setItem(AUTH_KEY, JSON.stringify(obj));
        return obj;
      }
    }
  } catch (e) {}
  return null;
}
export function getToken() { return getAuth(); }
export function clearAuth() { localStorage.removeItem(AUTH_KEY); }

export function logout() {
  clearAuth();
  location.hash = '#/login';
}

// 文件访问地址（静态文件需 token）
export function fileUrl(p) {
  if (!p) return '';
  const s = String(p).trim();
  if (!s) return '';
  // dataURL / blob / 绝对地址 / 已带 /uploads 前缀: 原样返回
  // (否则会被拼成 /uploads/data:image/... 这类非法路径, 浏览器直接裂图)
  if (/^(data:|blob:|https?:|\/\/)/i.test(s) || s.indexOf('/uploads/') === 0) return s;
  const auth = getAuth();
  return `/uploads/${s.replace(/^\/+/, '')}${auth ? '?token=' + auth.token : ''}`;
}

// 身份证缩略图: driverid/<年月>/xxx.jpeg -> driverid/thumbs/200x140/xxx.jpg
// 非 driverid 路径(含历史遗留的 base64)原样交给 fileUrl
export function idCardThumbUrl(p) {
  if (!p) return '';
  const s = String(p).trim();
  const m = /^driverid\/(?:[^/]+\/)?([^/]+)$/.exec(s);
  if (!m) return fileUrl(s);
  const stem = m[1].replace(/\.(jpe?g|png|webp)$/i, '');
  return fileUrl('driverid/thumbs/200x140/' + stem + '.jpg');
}

// blob 下载
export async function downloadFile(url, filename) {
  try {
    const auth = getAuth();
    const res = await axios.get(url, { responseType: 'blob', headers: auth ? { Authorization: 'Bearer ' + auth.token } : {} });
    const blob = new Blob([res.data]);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) {
    Toast.fail('下载失败');
  }
}

// 复制文本到剪贴板
export function copyText(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  }
  return new Promise((resolve, reject) => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    ta.style.top = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    try {
      document.execCommand('copy');
      resolve();
    } catch (e) {
      reject(e);
    } finally {
      document.body.removeChild(ta);
    }
  });
}

export default http;
